<template>
  <div id="settingsMenuBoard">
    <div v-if="ready">

      <div class="tabs">
        <div class="tab" v-for="(region, regionIndex) of definition.regions" @click="currentRegion = regionIndex"
             :class="{active: currentRegion === regionIndex}">
          {{$t('menuboard.screen') + ' nº' + (regionIndex + 1)}}
          <template v-if="region.id">&nbsp;({{region.id}})</template>
        </div>
        <div class="tab filler"></div>
        <div class="tab button" v-if="currentRegion !== null">
          <button class="btn btn-outline" @click="removeRegion(currentRegion)">{{$t('menuboard.remove_region')}}</button>
        </div>
        <div class="tab button">
          <button class="save btn btn-primary" @click="addNewRegion">{{$t("menuboard.add_region")}}</button>
        </div>
      </div>

      <br>

      <template v-for="(region, regionIndex) of definition.regions" v-if="currentRegion === regionIndex">
        <Panel :title="$t('menuboard.screen') + ' nº' + (regionIndex + 1)" collapsible>

          <div class="columns">
            <div class="column">
              <div class="subtitle-2">{{$t('configuration')}}</div>
              <div class="group">
                <label>{{$t('menuboard.region.id')}}</label>
                <input type="text" v-model="region.id" @keydown.space.prevent>
              </div>
              <div class="group">
                <label>{{$t('menuboard.region.template')}}</label>
                <select v-model="region.template" @change="templateChanged(region)">
                  <option value="">{{$t('choose')}}...</option>
                  <option :value="template" v-for="template of templates" :key="'template-' + template">{{$t('menuboard.template.' + template)}}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="subtitle-2">{{$t('menuboard.dimensions')}}</div>
              <div class="split">
                <div class="group">
                  <label>{{$t('menuboard.region.width')}}</label>
                  <input type="number" v-model="region.width">
                </div>
                <div class="x">&times;</div>
                <div class="group">
                  <label>{{$t('menuboard.region.height')}}</label>
                  <input type="number" v-model="region.height">
                </div>
              </div>
              <div class="group">
                <label>{{$t('menuboard.region.top')}}</label>
                <input type="number" v-model="region.top">
              </div>
              <div class="group">
                <label>{{$t('menuboard.region.left')}}</label>
                <input type="number" v-model="region.left">
              </div>
            </div>
          </div>

        </Panel>

        <Panel :title="$t('menuboard.constraints')" collapsible>
          <div class="constraint" v-if="region.time">
            <div class="subtitle-2 with-button">
              <span>{{$t('menuboard.constraint.time')}}</span>
              <div class="grow"></div>
              <button class="btn btn-outline" @click="removeTimeConstraint(region)">{{$t('menuboard.remove_constraint')}}</button>
            </div>
            <div class="time" v-for="(time, timeIndex) of region.time" :key="'time' + time">
              <div class="group">
                <label>{{$t('menuboard.range_start')}}</label>
                <input type="text" v-model="time[0]" placeholder="00:00">
              </div>
              <div class="group">
                <label>{{$t('menuboard.range_end')}}</label>
                <input type="text" v-model="time[1]" placeholder="00:00">
              </div>
              <div class="group">
                <button class="btn btn-outline btn-icon" @click="removeTimeRange(region, timeIndex)">
                  <svgicon src="icon/close.svg" :height="14" :width="14"></svgicon>
                </button>
              </div>
            </div>
            <div class="right">
              <button class="btn btn-primary btn-icon" @click="addTimeRange(region)">
                <svgicon src="icon/add.svg" :height="16" :width="16"></svgicon>
              </button>
            </div>
            <div class="hr"></div>
          </div>

          <div class="constraint" v-if="region.days">
            <div class="subtitle-2 with-button">
              <span>{{$t('menuboard.constraint.days')}}</span>
              <div class="grow"></div>
              <button class="btn btn-outline" @click="removeDaysConstraint(region)">{{$t('menuboard.remove_constraint')}}</button>
            </div>
            <div class="days">
              <div class="group" v-for="day of days" :key="'day' + day.id + regionIndex">
                <input type="checkbox" :id="'chk_' + day.name + regionIndex" :value="day.id" v-model="region.days" @change="$forceUpdate()">
                <label :for="'chk_' + day.name + regionIndex">{{$t(day.name)}}</label>
              </div>
            </div>
            <div class="hr"></div>
          </div>

          <template v-if="region.toggleInterval">
            <div class="subtitle-2 with-button">
              <span>{{$t('menuboard.constraint.toggleInterval')}}</span>
              <div class="grow"></div>
              <button class="btn btn-outline" @click="removeToggleIntervalConstraint(region)">{{$t('menuboard.remove_constraint')}}</button>
            </div>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{$t('menuboard.interval_delay')}}</label>
                  <input type="number" v-model="region.toggleInterval">
                </div>
              </div>
              <div class="column"></div>
            </div>
            <div class="hr"></div>
          </template>

          <div class="new-constraint columns">
            <div class="column">
              <div class="group">
                <label>{{$t('menuboard.add_constraint')}}</label>
                <select v-model="region.newConstraint">
                  <option :value="undefined">{{$t('choose')}}...</option>
                  <option v-if="!region[constraint]" :value="constraint" v-for="constraint of constraints">{{$t('menuboard.constraint.' + constraint)}}</option>
                </select>
              </div>
            </div>
            <div class="column right">
              <button class="btn btn-primary btn-icon" @click="addConstraint(region)">
                <svgicon src="icon/add.svg" :height="16" :width="16"></svgicon>
              </button>
            </div>
          </div>

        </Panel>

        <Panel v-if="region.template" :title="$t('menuboard.template_config') + ' - ' + $t('menuboard.template.' + region.template)">

          <template v-if="region.template === 'fullscreen-image'">
            <div class="subtitle-2">{{$t('menuboard.file_select')}}</div>
            <div class="description">{{$t('menuboard.file_select_description')}}</div>
            <BranchMediaSelector ref="imageMediaSelector" image :selected="region.image" @changed="selectedImage(region, $event)"></BranchMediaSelector>
          </template>

          <template v-if="region.template === 'fullscreen-video'">
            <div class="subtitle-2">{{$t('menuboard.file_select')}}</div>
            <div class="description">{{$t('menuboard.file_select_description')}}</div>
            <BranchMediaSelector ref="videoMediaSelector" video :selected="region.video" @changed="selectedVideo(region, $event)"></BranchMediaSelector>
            <div class="subtitle-2">{{$t('menuboard.external_urls')}}</div>
            <div class="description">{{$t('menuboard.external_urls_description')}}</div>
            <div class="urls">
              <div class="url" :class="{selected: region.videoUrl === url.url}"
                   @click="selectedVideoUrl(region, url.url, $event)"
                   v-for="(url, urlIndex) of region.urls" :key="'url' + urlIndex + region.id">
                <div class="url-name">
                  <label>{{$t('name')}}</label>
                  <input type="text" v-model="region.urls[urlIndex].name">
                </div>
                <div class="url-path">
                  <label>URL</label>
                  <input type="text" v-model="region.urls[urlIndex].url">
                </div>
                <button class="btn btn-primary" @click="removeUrl(region, url.url)">
                  <svgicon src="icon/close.svg" :height="14" :width="14"></svgicon>
                </button>
                <svgicon class="check" v-if="region.videoUrl === url.url"
                         src="icon/check-circle-full.svg" :height="17" :width="17"></svgicon>
              </div>
            </div>
            <button class="btn btn-primary" @click="addUrl(region)">{{$t('add')}}</button>
          </template>

          <template v-if="region.template === 'slideshow'">
            <div class="subtitle-2">{{$t('menuboard.files_select')}}</div>
            <div class="description">{{$t('menuboard.files_select_description')}}</div>
            <BranchMediaSelector ref="slideshowMediaSelector" multiple
                                 :selected="getSlideshowAssetIds(region.slideshow)"
                                 @changed="selectedSlideshow(region, $event)"></BranchMediaSelector>
            <br>
            <div class="subtitle-2">{{$t('menuboard.external_urls')}}</div>
            <div class="description">{{$t('menuboard.external_urls_description')}}</div>
            <div class="urls">
              <div class="url" :class="{selected: slideshowHasUrl(region.slideshow, url.url)}"
                   @click="selectedSlideshowUrl(region, url.url, $event)"
                   v-for="(url, urlIndex) of region.urls" :key="'url' + urlIndex + region.id">
                <div class="url-name">
                  <label>{{$t('name')}}</label>
                  <input type="text" v-model="region.urls[urlIndex].name">
                </div>
                <div class="url-path">
                  <label>URL</label>
                  <input type="text" v-model="region.urls[urlIndex].url">
                </div>
                <button class="btn btn-primary" @click="removeUrl(region, url.url)">
                  <svgicon src="icon/close.svg" :height="14" :width="14"></svgicon>
                </button>
                <svgicon class="check" v-if="slideshowHasUrl(region.slideshow, url.url)"
                         src="icon/check-circle-full.svg" :height="17" :width="17"></svgicon>
              </div>
            </div>
            <button class="btn btn-primary" @click="addUrl(region)">{{$t('add')}}</button>
            <br>
            <br>
            <div class="subtitle-2">{{$t('menuboard.display_order')}}</div>
            <div class="description">{{$t('menuboard.display_desc')}}</div>
            <Draggable v-model="region.slideshow" @start="drag = true" @end="drag = false">
              <div class="display-item"  v-for="(slide, index) of region.slideshow">
                <div class="icon">
                  <svgicon src="icon/hamburger.svg" :height="12" :width="12"></svgicon>
                </div>
                <template v-if="slide.type === 'asset' && getFileWithId(slide.id)">
                  <div class="name"><span class="num">nº{{index + 1}}</span>{{getFileWithId(slide.id).name}}</div>
                  <Tag>{{getFileWithId(slide.id).type}}</Tag>
                </template>
                <template v-if="slide.type === 'url'">
                  <div class="name"><span class="num">nº{{index + 1}}</span>{{getUrlName(region, slide.url)}}</div>
                  <Tag>URL</Tag>
                </template>
              </div>
            </Draggable>
            <br>
            <div class="subtitle-2">{{$t('configuration')}}</div>
            <div class="group">
              <label>{{$t('menuboard.slideshow_picture_duration')}}</label>
              <input type="text" v-model="region.imageDuration">
            </div>
          </template>

        </Panel>

      </template>

      <Panel :title="$t('settings.advanced_configurations')" collapsed collapsible v-if="currentRegion === 0">
        <div class="subtitle-2">{{$t('menuboard.copy_to_companies')}}</div>
        <div class="description">{{$t('menuboard.copy_description')}}</div>
        <ListMultiSelect :bank="companies" :bankTitle="$t('companies.title')" showSelectAll showUnselectAll
                         :selected="selectedCompanies" :selectedTitle="$t('menuboard.selected_companies')"/>
        <div class="transfer-button" v-if="selectedCompanies.length > 0">
          <button class="btn btn-primary" @click="copyToCompanies">{{$t('menuboard.copy_to_selected_companies')}}</button>
        </div>
      </Panel>

      <div class="footer-actions" v-if="definition.regions.length > 0">
        <button class="btn btn-primary" @click="save">{{$t("save")}}</button>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions } from "vuex";
import Draggable from 'vuedraggable';
import Tag from "@/components/tools/Tag.vue";
import ListMultiSelect from "@/components/tools/ListMultiSelect";
import BranchMediaSelector from "@/components/branch/BranchMediaSelector.vue";
import {Core} from "../../../core/Core";

export default {

  name: "SettingsMenuBoard",

  components: { BranchMediaSelector, Draggable, Tag, ListMultiSelect },

  data() {
    return {
      company: null,
      definition: null,
      companies: [],
      selectedCompanies: [],
      currentRegion: 0,
      templates: ['fullscreen-image', 'fullscreen-video', 'slideshow'],
      constraints: ['time', 'days', 'toggleInterval'],
      days: [
        {name: 'sunday', id: 0},
        {name: 'monday', id: 1},
        {name: 'tuesday', id: 2},
        {name: 'wednesday', id: 3},
        {name: 'thursday', id: 4},
        {name: 'friday', id: 5},
        {name: 'saturday', id: 6}
        ],
      ready: false
    }
  },

  computed: {
    branchId() {
      return this.$route.query.branch;
    },
    companyId() {
      return this.$route.query.company;
    }
  },

  watch: {
    currentRegion() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    }
  },

  async mounted() {
    this.$store.state.pageTitleTranslationKey = "settings.menuboard";
    this.$store.state.pageIcon = "";

    showSpinner();
    this.company = await this.$store.getters.getContextCompany;
    if (this.companyId) {
      await this.getCompanyConfig();
      await this.loadCompanies();
      this.ready = true;
    } else {
      this.$router.push("/");
    }
    hideSpinner();
  },

  methods: {
    ...mapActions(["getCompany", "saveCompany", "getCompanies"]),

    async getCompanyConfig() {
      let config = await this.getCompany({branchId: this.branchId, companyId: this.companyId, fallback: true});
      this.definition = config && config.menuBoard ? config.menuBoard.definition : null;
      if (!this.definition) {
        this.definition = {
          shop: this.company.nameCanonical,
          regions: []
        }
      } else {
        for (let region of this.definition.regions) {
          if (region.slideshow && !region.slideshow.find(s => typeof s === "object")) {
            region.slideshow = region.slideshow.map(s => { return { type: "asset", id: s }});
          }
          if ((region.template === "slideshow" || region.template === "fullscreen-video") && !region.urls) {
            region.urls = [];
          }
        }
      }
    },

    async loadCompanies() {
      let companies = await this.getCompanies(this.branchId);
      this.companies = (companies || [])
          .filter(c => c.id !== parseInt(this.companyId))
          .map(c => { return {name: c.name, nameCanonical: c.nameCanonical, id: c.id}});
    },

    slideshowHasUrl(slideshow, url) {
      return slideshow.find(s => s.type === 'url' && s.url === url);
    },

    getSlideshowAssetIds(slideshow) {
      return slideshow.filter(s => s.type === 'asset').map(s => s.id);
    },

    addUrl(region) {
      region.urls.push({ name: "", url: "" });
      this.$forceUpdate();
    },

    removeUrl(region, index) {
      region.urls.splice(index, 1);
      this.$forceUpdate();
    },

    /**
     * SCREEN DEFINITION
     * id {string} - Identifiant unique de la région
     * template {string} - Nom du gabarit à utiliser pour cette région
     * left {number} - Position horizontale, en pixels, de la région
     * top {number} - Position verticale, en pixels, de la région
     * width {number} - Largeur, en pixels, de la région
     * height {number} - Hauteur, en pixels, de la région
     * time {[[ string, string ]]} - Intervalles de temps ou la région est affichée, sous le format hh:mm
     * days {number[]} - Jours pendant lesquels la région est affichée, de 0 (dimanche) à 6 (samedi)
     * toggleInterval {number} - Délai en secondes qui permet d'afficher et cacher une région par intervale. Ex.: 15 secondes affichées, puis 15 secondes cachées.
     */
    /**
     * EXAMPLES
     * "time": [["00:00", "08:45"], ["09:31", "24:00"]],
     * "days": [1, 2, 3, 4, 5]
     */
    addNewRegion() {
      this.definition.regions.push({
        id: "",
        template: "",
        left: 0,
        top: 0,
        width: 1920,
        height: 1080,
        time: null,
        days: null,
        toggleInterval: null
      });
    },

    selectedImage(region, image) {
      region.image = image;
      this.$refs.imageMediaSelector[0].$forceUpdate();
      this.$forceUpdate();
    },

    selectedVideo(region, video) {
      region.video = video;
      region.videoUrl = null;
      this.$refs.videoMediaSelector[0].$forceUpdate();
      this.$forceUpdate();
    },

    selectedVideoUrl(region, url, event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'BUTTON' || !url) {
        return;
      }
      region.videoUrl = url;
      region.video = null;
      this.$refs.videoMediaSelector[0].$forceUpdate();
      this.$forceUpdate();
    },

    selectedSlideshowUrl(region, url, event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'BUTTON' || !url) {
        return;
      }
      let found = region.slideshow.find(s => s.type === "url" && s.url === url);
      if (found) {
        let index = region.slideshow.indexOf(found);
        region.slideshow.splice(index, 1);
      } else {
        region.slideshow.push({type: "url", url: url});
      }
    },

    selectedSlideshow(region, assetIds) {
      let currentIds = this.getSlideshowAssetIds(region.slideshow);
      for (let id of assetIds) {
        if (!currentIds.find(i => i === id)) {
          region.slideshow.push({type: "asset", id: id});
        }
      }
      for (let id of currentIds) {
        if (!assetIds.find(i => i === id)) {
          let slide = region.slideshow.find(s => s.id === id && s.type === "asset");
          let index = region.slideshow.indexOf(slide);
          if (index > -1) {
            region.slideshow.splice(index, 1);
          }
        }
      }
      this.$refs.slideshowMediaSelector[0].$forceUpdate();
      this.$forceUpdate();
    },

    addTimeRange(region) {
      region.time.push(["", ""]);
      this.$forceUpdate();
    },

    templateChanged(region) {
      switch (region.template) {
        case "fullscreen-image":
          delete region.video;
          delete region.videoUrl;
          delete region.imageDuration;
          delete region.slideshow;
          delete region.urls;
          region.image = null;
          break;
        case "fullscreen-video":
          delete region.image;
          delete region.imageDuration;
          delete region.slideshow;
          region.urls = region.urls || [];
          region.video = null;
          region.videoUrl = null;
          break;
        case "slideshow":
          delete region.image;
          delete region.video;
          delete region.videoUrl;
          region.slideshow = [];
          region.urls = region.urls || [];
          region.imageDuration = 8;
          break;
      }
    },

    removeRegion(regionIndex) {
      this.definition.regions.splice(regionIndex, 1);
      if (this.definition.regions.length > 0) {
        this.currentRegion = 0;
      } else {
        this.currentRegion = null;
      }
    },

    removeToggleIntervalConstraint(region) {
      region.toggleInterval = null;
      this.$forceUpdate();
    },

    removeTimeRange(region, index) {
      region.time.splice(index, 1);
      this.$forceUpdate();
    },

    removeTimeConstraint(region) {
      region.time = null;
      this.$forceUpdate();
    },

    removeDaysConstraint(region) {
      region.days = null;
      this.$forceUpdate();
    },

    async copyToCompanies() {
      if (!this.validateRegions()) {
        return;
      }
      let confirm = await showConfirmation(this.$t("warning"), this.$t("menuboard.copy_confirmation"));
      if (!confirm) {
        return;
      }
      showSpinner();
      let calls = [];
      let data = {
        menuBoard: {
          definition: this.parseBeforeSave(this.definition)
        }
      };
      for (let company of this.selectedCompanies) {
        data.menuBoard.definition.shop = company.nameCanonical;
        calls.push(Core.request("PUT",`/api/branches/${this.branchId}/companies/${company.id}`, deepCopy(data)))
      }
      try {
        await Promise.all(calls);
        hideSpinner();
        this.selectedCompanies = [];
        toast(this.$t("menuboard.copy_success"), "success", this.$t("success"));
      } catch (e) {
        hideSpinner();
        toast(this.$t("error.no_server"), "error");
        console.log("error", e);
      }
    },

    getFileWithId(id) {
      if (!this.ready || !this.$refs.slideshowMediaSelector) {
        return;
      }
      let ref = Array.isArray(this.$refs.slideshowMediaSelector) ? this.$refs.slideshowMediaSelector[0] : this.$refs.slideshowMediaSelector;
      if (ref) {
        return ref.getFileWithId(id);
      }
    },

    getUrlName(region, url) {
      let foundUrl = region.urls.find(u => u.url === url);
      if (foundUrl) {
        return foundUrl.name;
      }
    },

    addConstraint(region) {
      if (region.newConstraint) {
        switch (region.newConstraint) {
          case "time":
            region.time = [["", ""]];
            break;
          case "days":
            region.days = [];
            break;
          case "toggleInterval":
            region.toggleInterval = 8;
            break;
        }
       region.newConstraint = undefined;
      }
    },

    parseBeforeSave(data) {
      let copy = deepCopy(data);
      for (let region of copy.regions) {
        if (!region.time || region.time.length === []) {
          delete region.time;
        }
        if (!region.days || region.days.length === []) {
          delete region.days;
        } else {
          region.days = region.days.sort((a, b) => a - b);
        }
        if (!region.toggleInterval) {
          delete region.toggleInterval;
        }
        region.top = parseInt(region.top);
        region.left = parseInt(region.left);
        region.width = parseInt(region.width);
        region.height = parseInt(region.height);
      }
      return copy;
    },

    validateRegions() {
      for (let region of this.definition.regions) {
        if (!region.urls) {
          continue;
        }
        for (let url of region.urls) {
          if (!url.name || !isValidHttpUrl(url.url) || !url.url.endsWith(".mp4")) {
            showAffirmation(this.$t("error.title"), this.$t("menuboard.invalid_url"));
            return false;
          }
        }
      }
      return true;
    },

    async save() {
      if (!this.validateRegions()) {
        return;
      }
      let data = {
        menuBoard: {
          definition: this.parseBeforeSave(this.definition)
        }
      };
      let response = await this.saveCompany({
        branchId: this.branchId,
        companyId: this.companyId,
        data: data
      });
      if (response) {
        toast(this.$t("branch.success"), "success");
      } else {
        toast(this.$t("error.no_server"), "error");
      }
    }
  }


}

</script>

<style lang="scss" scoped>
  .hr {
    width: 100%;
    border-bottom: 1px dashed #ccc;
    margin: 30px 0 15px 0;
  }
  .urls {
    .url {
      display: flex;
      padding: 10px;
      border-radius: 5px;
      background-color: #f8f8f8;
      cursor: pointer;
      border: 1px solid #e9e9e9;
      margin-bottom: 15px;
      position: relative;

      &.selected {
        border-color: var(--primary-color);
        border-width: 2px;
      }

      .check {
        fill: var(--primary-color);
        position: absolute;
        right: -6px;
        top: 4px;
      }

      .url-name, .url-path {
        display: flex;
        flex-direction: column;

        label {
          font-size: 14px;
          margin-bottom: 4px;
          color: #888;
        }

        &:not(:last-child) {
          margin-right: 10px;
        }
      }

      .url-name {
        width: 200px;
      }

      .btn {
        height: 43px;
        width: 43px;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 18px;

        .svgicon {
          margin-right: 0;
        }
      }

      .url-path {
        flex-grow: 1;
      }
    }
  }
  .subtitle-2.with-button {
    align-items: center;
    display: flex;
    .btn {
      font-size: 14px;
    }
    .btn:not(:last-child) {
      margin-right: 10px;
    }
  }
  .transfer-button {
    text-align: right;
    margin-top: 15px;
  }
  .split {
    display: flex;
    align-items: center;
    .group {
      flex-grow: 1;
      flex-basis: 0;
    }
    .x {
      padding: 7px 10px 0 10px;
      color: #888;
      font-size: 17px;
    }
  }
  .description {
    color: #888;
    margin-bottom: 15px;
    font-size: 14px;
  }
  .grow {
    flex-grow: 1;
  }
  .days {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .right {
    display: flex;
    justify-content: flex-end;
  }
  .time {
    display: flex;
    align-items: center;
    .group {
      &:not(:last-child) {
        flex-grow: 1;
        margin-right: 10px;
      }
    }
  }
  .btn-icon {
    margin-top: 18px;
    height: 43px;
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;

    .svgicon {
      margin: 0;
    }
  }
  .display-item {
     padding: 10px;
     border-radius: 5px;
     background-color: #f8f8f8;
     cursor: grab;
     font-size: 12px;
     border: 1px solid #e9e9e9;
     font-weight: 500;
     max-width: 700px;
     width: 100%;
     display: flex;
     align-items: center;

     .icon {
       flex-shrink: 0;
       margin-right: 10px;
       fill: #c1c1c1;
     }

    .name {
      flex-grow: 1;
      .num {
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }

     .tag {
       margin-left: 10px;
       text-align: center;
       text-transform: none;
       font-weight: 500;
     }

     &:not(:last-child) {
       margin-bottom: 10px;
     }
     }
</style>

<style lang="scss">
  #settingsMenuBoard {
    .subtitle:not(:first-child) {
      padding-top: 15px;
    }
  }
</style>
